import { motion } from 'framer-motion';

import { Icon } from '@/new/components/icon';

const ITEM_HEIGHT = 13; // px, must match the line-height of each item
const ITEM_GAP = 8; // px, spacing between items
const ITEM_STEP = ITEM_HEIGHT + ITEM_GAP;
const PADDING_Y = 12; // px, top/bottom padding inside the container
const MAX_VISIBLE = 3;
const FULL_HEIGHT = MAX_VISIBLE * ITEM_HEIGHT + (MAX_VISIBLE - 1) * ITEM_GAP + 2 * PADDING_Y;

interface AiSearchWindowProps {
  items: string[];
  isFromDb?: boolean;
}

export function AiSearchWindow({ items, isFromDb }: AiSearchWindowProps) {
  // Container grows with items (1→2→3) then stays fixed; list scrolls once items exceed MAX_VISIBLE.
  const visibleCount = Math.max(1, Math.min(items.length, MAX_VISIBLE));
  const containerHeight = visibleCount * ITEM_HEIGHT + (visibleCount - 1) * ITEM_GAP + 2 * PADDING_Y;
  const topValue =
    items.length <= MAX_VISIBLE ? PADDING_Y : (FULL_HEIGHT - ITEM_HEIGHT) / 2 - (items.length - 2) * ITEM_STEP;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }} className="my-3">
      <div className="mb-1.5 flex items-center gap-2 text-xs text-gray-500">
        <Icon className={`${isFromDb ? 'lucide--search-check' : 'lucide--search'} shrink-0`} />
        <p>
          {isFromDb
            ? 'Analysed search results'
            : `Analysing ${items.length} matching ${items.length === 1 ? 'fact or document' : 'facts or documents'}...`}
        </p>
      </div>

      <motion.div
        className="relative overflow-hidden rounded-lg border border-gray-200 bg-white px-3"
        animate={{ height: containerHeight }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Fade at the top only shows if 4+ items */}

        <div
          className={`${items.length >= 4 ? 'opacity-100' : 'opacity-0'} absolute top-0 right-0 left-0 z-10 h-10 bg-gradient-to-b from-white/80 to-transparent duration-150`}
        />

        <motion.div
          className="absolute right-3 left-3"
          animate={{ top: topValue }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {items.map((item, i) => (
            <p
              key={i}
              style={{ height: ITEM_HEIGHT, lineHeight: `${ITEM_HEIGHT}px`, marginBottom: ITEM_GAP }}
              className="truncate text-xs text-gray-800"
            >
              {item}
            </p>
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
